import CsiFormData from "csi/action/FormData";
import { Context } from "csi/def/Context";
import { DojoViewProps } from "csi/page/DojoView";
import { getClass, isRevisionNode } from "csi/reflection/reflection";
import { localStore } from "csi/state/rootStore";
import { getLocalizedGroupLabelWithLocaleProvider } from "csi/store/common";
import { ICache } from "csi/typings";
import { ViewWidget } from "csi/view/Widget";
import Result from "csi/view/_query/Result";
import { ILocaleProvider } from "designSystem/locale";
import _ from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { getAggregateStoreResultKeysForNodeType } from "src/c8-platform/service/aggregateStoreService";
import { IAggregateStoreGroup, IAggregateStoreResponse, IAggregateStoreResult } from "src/c8-platform/type/aggregateStore";
import { ATTR_NAME, C8Node, C8NodeType } from "src/c8-platform/type/node";
import { cnl } from "src/c8-platform/type/value";

const INVALID_GROUP = "_invalid";

export interface IGoToDialogGroup {
	/** group from response (will only exist if using the aggregate store architecture) */
	aggregateGroup?: IAggregateStoreGroup;

	/** id for group */
	id: string;

	/** type of node in group */
	nodeType: C8NodeType;

	/** group results (can be empty initially, but will eventually be populated as view is loaded) */
	results: IAggregateStoreResult[];
}

export interface IGoToDialogTab {
	/** indicates if results for tab are known beforehand (false) or are determined dynamically as view is loaded */
	dynamicResults: boolean;

	/** true if either multiple groups, or single group has labelResource (when multiple tabs) */
	grouped?: boolean;

	/** groups belonging to tab (if not grouped, there will still be one group to track the results and selections) */
	groups: IGoToDialogGroup[];

	/** whether the results are ordered */
	ordered?: boolean;

	/** view will be filled in dynamically as it is loaded */
	view?: ViewWidget;

	/** properties to use to construct view in tab */
	viewProps: DojoViewProps;

}

/** return sorted list of selected values so comparison against new values is consistent */
function getFormDataValue(formData: CsiFormData): cnl[] {
	return formData.getAggregateValue()
		.sort();
}

class GoToDialogStore {
	@observable.ref private _currentValue: cnl[];
	private readonly _initialValue: cnl[];
	private readonly _invalidResults: IAggregateStoreResult[];
	@observable private _tabIndex = 0;
	private readonly _tabs: IGoToDialogTab[];
	private readonly _tabsViewed = observable.set<number>();
	@observable private _viewsLoaded = 0;

	/** aggregate store response */
	readonly aggregateStoreResponse?: IAggregateStoreResponse;

	/** cache for Go to Dialog views */
	readonly cache: ICache;

	/** force Save button to be enabled even if no change */
	readonly forceAllowSave: boolean;

	/** form data */
	readonly formData: CsiFormData;

	/** indicates if save button should be enabled */
	get allowSave(): boolean {
		const { forceAllowSave, hasRequiredSelections, _currentValue, _initialValue } = this;
		return forceAllowSave || (hasRequiredSelections && !_.isEqual(_currentValue, _initialValue));
	}

	/** indicates if selections meet minimum requirements */
	get hasRequiredSelections(): boolean {
		const { formData, numberOfSelections } = this;
		return !formData.m_bRequireOne || !!numberOfSelections;
	}

	/** determine if tabs are needed */
	get hasTabs(): boolean {
		return this.numberOfTabs > 1;
	}

	/** list of results from existing attribute value that are no longer valid but still selected */
	get invalidResults(): IAggregateStoreResult[] {
		const { _currentValue, _invalidResults } = this;
		const set = new Set(_currentValue);
		return _invalidResults.filter(({ $id }) => set.has($id));
	}

	/** indicates if first view is loaded which means dialog is ready to view */
	get isFirstViewLoaded(): boolean {
		return this._viewsLoaded > 0;
	}

	/** indicates that only one item can be selected - goto dialog from single select */
	get isSingleSelect(): boolean {
		const { formData } = this;
		return formData.m_bSelectOne;
	}

	/** number of selections */
	get numberOfSelections(): number {
		return this._currentValue.length;
	}

	/** number of tabs to display */
	get numberOfTabs(): number {
		return this._tabs.length;
	}

	/** selected tab */
	get selectedTab(): IGoToDialogTab {
		return this._tabs[this._tabIndex];
	}

	/** form data selection for overview pane */
	@computed get selectionsByGroup(): Record<string, IAggregateStoreResult[]> {
		const { cache, invalidResults, _currentValue, _tabs, _viewsLoaded } = this;
		const groupMap: Record<C8NodeType, IGoToDialogGroup> = {};
		const selectionSet = new Set(_currentValue);
		const selectionMap = {};

		// remove any invalid results from the selectionSet, and assign to the invalid group
		for (const { $id } of invalidResults) {
			selectionSet.delete($id);
		}

		selectionMap[INVALID_GROUP] = _.sortBy(invalidResults, [ATTR_NAME]);

		// populate other groups
		for (const tab of _tabs) {
			for (const group of tab.groups) {
				const { id, nodeType, results } = group;
				const selections = [];

				// if missing any results, then wait until at least first view is loaded to process selections;
				// once the view loads, we will know which results belong to it
				if (!results && !_viewsLoaded) {
					return selectionMap;
				}

				// for groups where not all results are known, selections will be allocated by type;
				// NOTE: this only makes sense if one group of that type
				if (tab.dynamicResults) {
					const { abstract, concreteClasses } = getClass(nodeType);

					// if abstract, then populate map with all concrete node types of class
					if (abstract) {
						concreteClasses.forEach((rc) => {
							groupMap[rc.type] = group;
						});
					} else {
						groupMap[nodeType] = group;
					}
				}

				results?.forEach((result) => {
					if (selectionSet.has(result.$id)) {
						selections.push(result);
						selectionSet.delete(result.$id);
					}
				});

				selectionMap[id] = selections;
			}
		}

		// determine remaining selections
		for (const url of selectionSet) {
			const node = cache.get(url);
			const group = groupMap[node?.$Type];

			if (group) {
				selectionMap[group.id].push({
					$id: url,
					[ATTR_NAME]: null,
				});
			}
		}

		return selectionMap;
	}

	/** selected tab index */
	get tabIndex(): number {
		return this._tabIndex;
	}

	/** tabs in dialog */
	get tabs(): IGoToDialogTab[] {
		return this._tabs;
	}

	/** return true if drawer with SelectionOverviewPane should be used */
	get useDrawer(): boolean {
		return !this.isSingleSelect;
	}

	/** return current selections for given group */
	private getGroupSelections = (tab: IGoToDialogTab, groupIndex: number, loc: ILocaleProvider): IAggregateStoreResult[] => {
		const { getResultNodeName, selectionsByGroup } = this;
		const { dynamicResults, groups, ordered = false } = tab;
		const { id } = groups[groupIndex];
		const selections = selectionsByGroup[id] || [];

		if (dynamicResults) {
			// populate result values of items if not already known and sort by combination of keys
			// NOTE: resultKeys are not currently supported with dynamic results
			selections.forEach((result) => {
				result[ATTR_NAME] = result[ATTR_NAME] ?? getResultNodeName(loc, result);
			});

			if (!ordered) {
				return _.sortBy(selections, [ATTR_NAME]);
			}
		}

		return selections;
	};

	/** return name of result from nodes in cache */
	private getResultNodeName = (loc: ILocaleProvider, result: IAggregateStoreResult): string => {
		const { aggregateStoreResponse, cache, formData: { useParentNameInSelectionOverview } } = this;
		const node = cache.get(result.$id);
		const name = loc.getNodeName(node);

		// aggregate stores (will) have logic to show an appropriate name for (revision/version) nodes (C8-48075, C8-49907),
		// but need logic here too when not using aggregate stores
		if (!aggregateStoreResponse && (useParentNameInSelectionOverview || isRevisionNode(node))) {
			const parent = cache.get(node.__Parent__);
			if (parent) {
				return `${loc.getNodeName(parent)} - ${name}`;
			}
		}

		return name;
	};

	/** return primary and secondary text for result */
	getResultText = (loc: ILocaleProvider, result: IAggregateStoreResult, nodeType?: C8NodeType): [primary: string, secondary?: string] => {
		const { aggregateStoreResponse } = this;
		const [key1, key2] = aggregateStoreResponse ? getAggregateStoreResultKeysForNodeType(aggregateStoreResponse, nodeType) : [ATTR_NAME];
		const name1 = loc.getNameOrUnnamed(result[key1]);
		return key2 ? [name1, loc.getNameOrUnnamed(result[key2])] : [name1];
	};

	/** return true if tab contains any groups with selections */
	getTabHasSelections = (tab: IGoToDialogTab): boolean => {
		const { selectionsByGroup } = this;
		return tab.groups.some(({ id }) => !!selectionsByGroup[id]?.length);
	};

	/** return label for given tab */
	getTabLabel = (tab: IGoToDialogTab, loc: ILocaleProvider): string => {
		const { grouped, groups } = tab;
		const { aggregateGroup, nodeType } = groups[0];

		// if tab view is not grouped, then just show aggregate group label
		if (aggregateGroup && !grouped) {
			return getLocalizedGroupLabelWithLocaleProvider(aggregateGroup, loc);
		}

		// TODO - should have the ability to customize the label
		// hard-code the type for now for SupplierFactory
		const finalNodeType = (nodeType === "SupplierFactory" ? "Factory" : nodeType);

		// otherwise, tab label is the plural type of the groups it contains (all groups will have same type)
		return loc.type(finalNodeType, true);
	};

	/** return grouped selections from tab groups */
	getTabSelections = (tab: IGoToDialogTab, loc: ILocaleProvider): IAggregateStoreResult[][] => {
		return tab.groups.map((group, index) => this.getGroupSelections(tab, index, loc));
	};

	/** return view properties for tab at given index */
	getViewProps = (index: number): DojoViewProps => {
		const tab = this._tabs[index];
		const { dynamicResults, groups, viewProps } = tab;
		const { viewParams, ...otherProps } = viewProps;

		if (dynamicResults) {
			// as view loads more results, need to track them
			const onViewResults = (qr: Result, resultNodes: C8Node[]): void => {
				const { results } = groups[0];
				const existingUrls = results.map(({ $id }) => $id);
				const existingSet = new Set(existingUrls);

				qr.store.resultUrls?.forEach((url) => {
					if (!existingSet.has(url)) {
						results.push({
							[ATTR_NAME]: null,
							$id: url,
						});
					}
				});
			};

			return {
				viewParams: {
					onViewResults,
					...viewParams,
				},
				...otherProps,
			};
		}

		return viewProps;
	};

	/** return true if any groups in tab are suggested */
	isTabSuggested = (tab: IGoToDialogTab): boolean => {
		return tab.groups.some(({ aggregateGroup }) => aggregateGroup.suggested);
	};

	/** indicate if tab index has already been viewed */
	hasViewedTab = (index: number): boolean => {
		return this._tabsViewed.has(index);
	};

	/** select / unselect node */
	@action changeSelection = (url: cnl, bChecked: boolean): void => {
		const { cache, formData } = this;
		const ctx = formData.getSource()
			.getContext()
			.copyContext();
		const node = cache.get(url);
		this.formData.changeSelection(ctx.setNode(node), bChecked);
	};

	/** unselect all  */
	@action clearSelection = (): void => {
		for (const url of this._currentValue) {
			this.changeSelection(url, false);
		}
	};

	/** record change to selections */
	@action onSelect = (ctx: Context, url: cnl, bChecked: boolean, bReplaced: boolean): void => {
		this._currentValue = getFormDataValue(this.formData);
	};

	/** indicate that first view is loaded */
	@action onChangeTab = (event: any, tabIndex: number): void => {
		// indicate previously selected view it is no longer selected
		this.selectedTab.view?.setTabNotSelected();

		// record tab change
		this._tabsViewed.add(tabIndex);
		this._tabIndex = tabIndex;

		// indicate new view is selected
		this.selectedTab.view?.setTabSelected();
	};

	/** track open state of right pane in local storage */
	@action onToggleOpen = (open: boolean): void => {
		localStore.gotoDialogDrawerOpen = open;
	};

	/** resize view after right frame open/close transition ends */
	onTransition = (): void => {
		this.selectedTab.view?.resize();
	};

	/** indicate that first view is loaded */
	@action onViewLoad = (view: ViewWidget): void => {
		++this._viewsLoaded;
		this.selectedTab.view = view;
	};

	constructor(
		aggregateStoreResponse: IAggregateStoreResponse,
		cache: ICache,
		formData: CsiFormData,
		invalidResults: IAggregateStoreResult[],
		tabs: IGoToDialogTab[],
		forceAllowSave = false,
	) {
		makeObservable(this);

		this.aggregateStoreResponse = aggregateStoreResponse;
		this.cache = cache;
		this.forceAllowSave = forceAllowSave;
		this.formData = formData;
		this._invalidResults = invalidResults;
		this._currentValue = this._initialValue = getFormDataValue(formData);
		this._tabs = tabs;
		this._tabsViewed.add(this.tabIndex);

		formData.addSelectListener(this.onSelect);
	}
}

export const GoToDialogStoreContext = createContext<GoToDialogStore>(undefined);
export const useGoToDialogStore = (): GoToDialogStore => useContext(GoToDialogStoreContext);

export default GoToDialogStore;